import { useQuery as useReactQueryQuery } from '@tanstack/react-query';
import type {
  QueryKey,
  UseQueryOptions as UseQueryOptionsReactQuery,
  QueryFunction,
  QueryFunctionContext,
} from '@tanstack/react-query';
import type { SetRequired } from 'type-fest';

/**
 * Generic return type for query functions.
 */
export type QueryResponse<ResponseBody> = Response & {
  data?: ResponseBody;
};

//  Like the usual useQuery options, but marks the queryFn as required (it should always be provided).
type UseQueryOptions<
  ResponseBody = unknown,
  ResponseError = Error,
  TData = ResponseBody,
  TQueryKey extends QueryKey = QueryKey,
> = SetRequired<
  UseQueryOptionsReactQuery<QueryResponse<ResponseBody>, ResponseError, TData, TQueryKey>,
  'queryFn'
>;

/**
 * A method to extract the response data from a query request.
 * Note that errors will continue to bubble up to be thrown by React Query,
 * and should be handled in the consuming component.
 *
 * @param {() => Promise<{ data?: ResponseBody }>} request - The request to make.
 */
async function getResponseData<ResponseBody = unknown, TQueryKey extends QueryKey = QueryKey>(
  request: QueryFunction<QueryResponse<ResponseBody>, TQueryKey>,
  context: QueryFunctionContext<TQueryKey>, // This needs to be passed through to the request function, in case it is used.
): Promise<ResponseBody | undefined> {
  const response = await request(context);
  return response?.data ?? undefined;
}

/**
 * Wrapper around the default useQuery hook that provides improved types.
 * Specifically, it will resolve the response.data and return that, so that
 * you don't need to read `data.data` in the consuming component.
 */
export default function useQuery<
  ResponseBody = unknown,
  ResponseError = Error,
  TData = ResponseBody,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<ResponseBody, ResponseError, TData, TQueryKey>, shouldParseResponseData = true) {
  const queryFn = (context: QueryFunctionContext<TQueryKey>) => {
    if (shouldParseResponseData) {
      return getResponseData<ResponseBody, TQueryKey>(options.queryFn, context);
    }
    return options.queryFn(context);
  };
  const queryOptions: UseQueryOptions<ResponseBody, ResponseError, TData, TQueryKey> = {
    ...options,
    //  TODO: Remove this cast when the types are fixed in react-query.
    queryFn: queryFn as unknown as QueryFunction<QueryResponse<ResponseBody>, TQueryKey>,
  };
  return useReactQueryQuery(queryOptions);
}
